import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image, Film, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';

interface Props {
  onUpload: (url: string, publicId: string) => void;
  accept?: string;
  label?: string;
  currentUrl?: string;
  folder?: string;
  maxSizeMb?: number;
  type?: 'image' | 'video' | 'document' | 'any';
}

type UploadState = 'idle' | 'dragging' | 'uploading' | 'success' | 'error';

const CloudinaryUpload: React.FC<Props> = ({
  onUpload,
  accept,
  label = 'Upload File',
  currentUrl = '',
  folder = 'erue-festival',
  maxSizeMb = 10,
  type = 'image',
}) => {
  const [state, setState]       = useState<UploadState>('idle');
  const [preview, setPreview]   = useState<string>(currentUrl);
  const [error, setError]       = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [fileName, setFileName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptMap: Record<string, string> = {
    image:    'image/jpeg,image/png,image/webp,image/gif',
    video:    'video/mp4,video/webm,video/mov',
    document: 'application/pdf,.doc,.docx',
    any:      'image/*,video/*,application/pdf',
  };

  const acceptStr = accept || acceptMap[type] || 'image/*';

  const uploadFile = useCallback(async (file: File) => {
    // Validate size
    if (file.size > maxSizeMb * 1024 * 1024) {
      setError(`File too large. Max ${maxSizeMb}MB.`);
      setState('error');
      return;
    }

    setState('uploading');
    setProgress(0);
    setError('');
    setFileName(file.name);

    // Show local preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      formData.append('resourceType', file.type.startsWith('video/') ? 'video' : 'auto');

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(p => Math.min(p + 10, 85));
      }, 200);

      const r = await fetch('/api/community-portals/upload', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);

      const d = await r.json();

      if (d.success) {
        setState('success');
        setPreview(d.url);
        onUpload(d.url, d.publicId || '');
      } else {
        setError(d.error || 'Upload failed');
        setState('error');
      }
    } catch {
      setError('Network error — upload failed');
      setState('error');
    }
  }, [folder, maxSizeMb, onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setState('idle');
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setState('dragging'); };
  const handleDragLeave = () => setState('idle');
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };
  const handleClear = () => {
    setPreview(''); setState('idle'); setError(''); setProgress(0); setFileName('');
    onUpload('', '');
    if (inputRef.current) inputRef.current.value = '';
  };

  const iconForType = () => {
    if (type === 'video')    return <Film className="w-8 h-8" />;
    if (type === 'document') return <FileText className="w-8 h-8" />;
    return <Image className="w-8 h-8" />;
  };

  return (
    <div className="space-y-2">
      {label && <label className="text-sm text-slate-300 block">{label}</label>}

      {/* Preview area */}
      {preview && state !== 'error' && (
        <div className="relative w-full rounded-lg overflow-hidden border border-slate-600 bg-slate-900">
          {type === 'video' ? (
            <video src={preview} controls
              className="w-full max-h-48 object-cover" />
          ) : type === 'document' ? (
            <div className="flex items-center gap-3 p-3">
              <FileText className="w-8 h-8 text-[#D4AF37]" />
              <span className="text-sm text-white truncate">{fileName || 'Document uploaded'}</span>
            </div>
          ) : (
            <img src={preview} alt="Preview"
              className="w-full max-h-48 object-cover" />
          )}
          <button
            onClick={handleClear}
            className="absolute top-2 right-2 bg-slate-900/80 rounded-full p-1
                       text-slate-300 hover:text-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          {state === 'success' && (
            <div className="absolute bottom-2 right-2 bg-green-900/80 rounded-full p-1">
              <CheckCircle className="w-4 h-4 text-green-400" />
            </div>
          )}
        </div>
      )}

      {/* Upload zone */}
      {(!preview || state === 'error') && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
          className={`
            w-full border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
            transition-all duration-200 select-none
            ${state === 'dragging'
              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
              : state === 'error'
              ? 'border-red-600 bg-red-900/10'
              : 'border-slate-600 bg-slate-900/40 hover:border-slate-500 hover:bg-slate-800/40'}
          `}
        >
          {state === 'uploading' ? (
            <div className="space-y-3">
              <Loader className="w-8 h-8 text-[#D4AF37] mx-auto animate-spin" />
              <p className="text-sm text-slate-300">Uploading {fileName}…</p>
              <div className="w-full bg-slate-700 rounded-full h-1.5">
                <div
                  className="bg-[#D4AF37] h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-slate-500">{progress}%</p>
            </div>
          ) : state === 'error' ? (
            <div className="space-y-2">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
              <p className="text-sm text-red-400">{error}</p>
              <p className="text-xs text-slate-500">Click to try again</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-slate-500 mx-auto w-fit">
                {iconForType()}
              </div>
              <p className="text-sm text-slate-300">
                {state === 'dragging' ? 'Drop to upload' : 'Drag & drop or click to browse'}
              </p>
              <p className="text-xs text-slate-500">
                Max {maxSizeMb}MB
                {type === 'image'    ? ' · JPG, PNG, WebP' : ''}
                {type === 'video'    ? ' · MP4, WebM, MOV' : ''}
                {type === 'document' ? ' · PDF, DOC' : ''}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Change button when preview exists and not uploading */}
      {preview && state === 'success' && (
        <button
          onClick={() => inputRef.current?.click()}
          className="text-xs text-slate-400 hover:text-[#D4AF37] transition-colors"
        >
          Replace file
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptStr}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default CloudinaryUpload;
